import { useEffect, useState } from "react";
import { AlertCircle, GitBranch } from "lucide-react";
import api from "../../lib/api";
import { architectureError, isTransversal, nodeFacts, relationTitle, sortGroups, transitionRows, viaText } from "../../lib/architecture";
import { ConfidenceBadge, KindBadge, SourceRef } from "./ArchitectureBits";
import RelationGraph from "./RelationGraph";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { MobileSheet, MobileSheetBody, MobileSheetContent, MobileSheetHeader, MobileSheetTitle } from "../ui/mobile-sheet";

const PAGE = 20;

function RelationGroup({ group, onOpen }) {
  const [all, setAll] = useState(false);
  const items = all ? group.items : group.items.slice(0, PAGE);
  return (
    <section className="py-2" data-testid="relation-group" data-kind={group.kind} data-direction={group.direction}>
      <h4 className="mb-1 text-xs font-semibold text-slate-700">
        {relationTitle(group.kind, group.direction)} <span className="font-normal text-slate-500">({group.total})</span>
      </h4>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 bg-white">
        {items.map((relation) => (
          <li key={`${relation.other.id}-${relation.evidence.path}-${relation.evidence.line}`} className="px-3 py-2" data-testid="relation-item" data-confidence={relation.confidence}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <button type="button" onClick={() => onOpen(relation.other.id)}
                className="min-w-0 break-all text-left text-sm font-medium text-jamora hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:min-h-11"
                data-testid="relation-open">
                {relation.other.name}
              </button>
              <KindBadge kind={relation.other.kind} />
              <ConfidenceBadge value={relation.confidence} />
              {isTransversal(relation) ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs text-slate-600" title="Collection traversée par presque toutes les requêtes">transversale</span> : null}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
              <span>Preuve :</span>
              <SourceRef path={relation.evidence.path} line={relation.evidence.line} />
            </div>
            {viaText(relation) ? <p className="mt-0.5 break-words text-xs text-slate-500">Chemin : {viaText(relation)}</p> : null}
          </li>
        ))}
      </ul>
      {group.items.length > PAGE && !all ? (
        <button type="button" onClick={() => setAll(true)} className="mt-1 text-xs font-medium text-jamora hover:underline">Voir les {group.items.length - PAGE} autres</button>
      ) : null}
      {group.total > group.items.length ? <p className="mt-1 text-xs text-slate-500">{group.total - group.items.length} autres relations non listées (limite d'affichage).</p> : null}
    </section>
  );
}

function Body({ data, graph, wide, view, onView, onOpen, onExplore }) {
  const { node, relations, unknowns } = data;
  const facts = nodeFacts(node);
  const transitions = node.kind === "state_table" ? transitionRows(node) : [];
  const groups = sortGroups(relations);
  return (
    <div className="space-y-1 px-4 pb-6" data-testid="node-body">
      <section className="py-3 border-b border-slate-100">
        <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">Dans le code</h3>
        <SourceRef path={node.path} line={node.line} testid="node-source" />
        <button type="button" onClick={() => onExplore(node.id)} data-testid="node-explore"
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-2.5 h-9 text-xs font-medium text-slate-800 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-jamora [@media(pointer:coarse)]:h-11">
          <GitBranch size={14} aria-hidden />Explorer le lignage
        </button>
        <p className="mt-1 text-xs text-slate-500">Commit {data.snapshot.commit_sha.slice(0, 12)}. L'instantané garde des chemins et des numéros de ligne, jamais le code lui-même.</p>
      </section>
      {facts.length ? (
        <section className="py-3 border-b border-slate-100">
          <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">Ce que dit le registre</h3>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1 text-sm" data-testid="node-facts">
            {facts.map(([label, value]) => (<div key={label} className="contents"><dt className="text-slate-500">{label}</dt><dd className="break-words text-slate-900">{value}</dd></div>))}
          </dl>
        </section>
      ) : null}
      {transitions.length ? (
        <section className="py-3 border-b border-slate-100" data-testid="node-transitions">
          <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">États et transitions</h3>
          <ul className="space-y-0.5 text-sm">
            {transitions.map(([from, to]) => (<li key={from}><code className="rounded bg-slate-100 px-1">{from}</code> → {to.length ? to.map((t) => (<code key={t} className="mr-1 rounded bg-slate-100 px-1">{t}</code>)) : <span className="text-slate-500">état final</span>}</li>))}
          </ul>
        </section>
      ) : null}
      <section className="py-3" data-testid="node-relations">
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-2xs font-semibold uppercase tracking-wider text-slate-500">Relations</h3>
          {wide ? (
            <div role="group" aria-label="Affichage des relations" className="inline-flex rounded-md border border-slate-200 p-0.5 text-xs">
              {[["graph", "Schéma"], ["list", "Liste"]].map(([id, label]) => (
                <button key={id} type="button" aria-pressed={view === id} onClick={() => onView(id)} data-testid={`view-${id}`}
                  className={`rounded px-2 py-1 ${view === id ? "bg-jamora text-white" : "text-slate-700 hover:bg-slate-100"}`}>{label}</button>
              ))}
            </div>
          ) : null}
        </div>
        {wide && view === "graph" && graph ? <div className="mb-3 rounded-md border border-slate-200 bg-slate-50/50 p-2"><RelationGraph graph={graph} onOpen={onOpen} /></div> : null}
        {groups.length ? groups.map((g) => (<RelationGroup key={`${g.direction}:${g.kind}`} group={g} onOpen={onOpen} />)) : (
          <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-600" data-testid="node-no-relation">Aucune relation connue pour cet élément. Ce n'est pas une preuve qu'il n'en a pas : voir « À investiguer » pour ce que l'analyse n'a pas pu trancher.</p>
        )}
      </section>
      {unknowns.length ? (
        <section className="py-3 border-t border-slate-100" data-testid="node-unknowns">
          <h3 className="mb-1.5 text-2xs font-semibold uppercase tracking-wider text-slate-500">À investiguer pour cet élément</h3>
          <ul className="space-y-1.5 text-sm">
            {unknowns.map((u, i) => (<li key={i} className="rounded-md bg-slate-50 p-2"><span className="font-medium">{u.kind.replace(/_/g, " ")}</span> — <span className="break-words text-slate-700">{u.detail}</span></li>))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

// Detail of one element of the map: where it is in the code, what the registry says about it, then its relations in both directions, each with its proof
// and confidence. A side drawer on a computer, a bottom sheet on a phone (the overlay rule of the CRM).
export default function NodePanel({ nodeId, snapshot, mode, view, onView, onOpen, onExplore, onClose }) {
  const [state, setState] = useState({ phase: "loading" });
  const [graph, setGraph] = useState(null);
  const wide = mode === "drawer";
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    setState({ phase: "loading" });
    setGraph(null);
    const params = { id: nodeId, ...(snapshot ? { snapshot } : {}) };
    api.get("/architecture/node", { params })
      .then(({ data }) => live && setState({ phase: "ready", data }))
      .catch((e) => live && setState({ phase: e?.response?.status === 404 ? "missing" : "error", message: architectureError(e, "Impossible de charger cet élément. Réessayez dans quelques instants.") }));
    return () => { live = false; };
  }, [nodeId, snapshot, attempt]);

  useEffect(() => {
    if (!wide || view !== "graph" || state.phase !== "ready") return undefined;
    let live = true;
    api.get("/architecture/graph", { params: { id: nodeId, ...(snapshot ? { snapshot } : {}) } }).then(({ data }) => live && setGraph(data)).catch(() => live && setGraph(null));
    return () => { live = false; };
  }, [wide, view, state.phase, nodeId, snapshot]);

  const node = state.data?.node;
  const title = node ? node.name : state.phase === "loading" ? "Chargement…" : "Élément";
  const header = (Tag, Title) => (
    <div className={`border-b border-slate-200 px-4 pb-3 pt-4 ${wide ? "pr-12" : ""}`} data-testid="node-header">
      <Title asChild><Tag tabIndex={-1} className="break-all text-base font-semibold leading-snug text-slate-900 outline-none" data-testid="node-title">{title}</Tag></Title>
      {node ? (
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <KindBadge kind={node.kind} />
          <ConfidenceBadge value={node.confidence} testid="node-confidence" />
          {node.status && node.status !== "mapped" ? <span className="rounded bg-amber-50 px-1.5 py-0.5 text-2xs font-medium text-amber-800">{node.status === "unresolved" ? "non résolu" : node.status}</span> : null}
          {(node.metadata?.application === false) ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-2xs text-slate-600" title="Observé seulement dans des tests, scripts ou harnais">hors application</span> : null}
        </div>
      ) : null}
    </div>
  );
  const content = state.phase === "loading" ? (
    <div className="space-y-3 p-4" data-testid="node-loading"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-16 w-full" /><Skeleton className="h-32 w-full" /></div>
  ) : state.phase === "ready" ? (
    <Body data={state.data} graph={graph} wide={wide} view={view} onView={onView} onOpen={onOpen} onExplore={onExplore} />
  ) : (
    <div className="m-4 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700" role="alert" data-testid="node-error">
      <p className="flex items-start gap-2"><AlertCircle size={16} className="mt-0.5 shrink-0" aria-hidden />{state.phase === "missing" ? "Cet élément n'existe pas dans cet instantané (il a peut-être été renommé ou supprimé)." : state.message}</p>
      {state.phase === "error" ? <Button variant="outline" size="sm" className="mt-3" onClick={() => setAttempt((n) => n + 1)}>Réessayer</Button> : null}
    </div>
  );

  if (wide) {
    return (
      <Sheet open onOpenChange={(v) => { if (!v) onClose(); }}>
        <SheetContent side="right" className="flex w-[640px] max-w-full flex-col gap-0 p-0 sm:max-w-[640px]" data-testid="node-panel" data-mode="drawer" aria-describedby={undefined}>
          <SheetDescription className="sr-only">Détail d'un élément de la cartographie du système</SheetDescription>
          {header("h2", SheetTitle)}
          <div className="min-h-0 flex-1 overflow-y-auto">{content}</div>
        </SheetContent>
      </Sheet>
    );
  }
  return (
    <MobileSheet open onOpenChange={(v) => { if (!v) onClose(); }}>
      <MobileSheetContent closeOnHandle data-testid="node-panel" data-mode="sheet" aria-describedby={undefined}>
        <MobileSheetHeader className="block p-0">{header("h2", MobileSheetTitle)}</MobileSheetHeader>
        <MobileSheetBody className="p-0">{content}</MobileSheetBody>
      </MobileSheetContent>
    </MobileSheet>
  );
}
